<template>
  <div v-if="submitted && submittedHtml" v-html="submittedHtml"></div>
  <div
    v-if="!submitted"
    class="relative flex flex-col overflow-hidden rounded-md border-t-[6px] border-t-dark bg-white shadow-3 lg:flex-row"
  >
    <site-loader v-if="submitting" :background="true" />
    <div class="relative flex w-full flex-col p-5">
      <div class="mb-5 flex items-center justify-between">
        <h3 class="mb-0 text-xl font-semibold sm:text-3xl">
          <span v-if="step == 1">{{ t("{type} products", { type: typeTitle }) }}</span>
          <span v-if="step == 2">{{ t("Enter details") }}</span>
        </h3>
        <div class="flex items-center text-sm">
          <a v-if="step > 1" href="#" class="mr-1.5 inline-flex text-red-500" @click.prevent="step = step - 1">
            <span class="icon slider-arrow-next size-5 rotate-180"></span>
          </a>
          <span class="hidden text-sm sm:inline">{{ t("Step {step} of 2", { step: step }) }}</span>
        </div>
      </div>
      <ul v-if="persons.length > 1" class="persons-menu mb-5 flex border-b border-dark">
        <li
          v-for="(person, index) in persons"
          :key="index"
          :class="
            'cursor-pointer px-3.5 py-1.5 text-sm font-medium text-dark' +
            (selectedPerson == person.uniqueId ? ' bg-dark text-white' : '')
          "
          @click="selectedPerson = person.uniqueId"
        >
          <span class="title">{{ getName(person, index) }}</span>
          <span v-if="index > 0" class="icon close-icon ms-2.5 size-2.5" @click="removePerson(person.uniqueId)"></span>
        </li>
      </ul>
      <calculator-person-additional
        v-for="person in persons"
        v-show="selectedPerson == person.uniqueId"
        :key="person.uniqueId"
        :step="step"
        :person="person"
        :initial-postcode="initialPostcode"
        :type-id="typeId"
        :other-type-ids="otherTypeIds"
        :errors="errors[person.uniqueId] || []"
        @update="updatePerson($event)"
      />
      <div class="flex justify-end">
        <a v-if="step == 1" href="#" class="text-sm text-red-500" @click.prevent="addPerson">{{ t("Add a person") }}</a>
      </div>
    </div>
    <div class="flex w-full shrink-0 flex-col justify-between bg-dark p-5 py-7 text-white lg:w-[30%]">
      <div>
        <p class="mb-2.5 text-xl font-semibold">{{ t("Exact Calculation") }}</p>
        <p class="text-xs text-dark-1">
          {{
            t("Price per month in {currency}", {
              currency: currency(),
            })
          }}
        </p>
        <div class="flex flex-col text-sm font-medium text-dark-10">
          <div v-for="(person, index) in persons" :key="index">
            <div v-if="person.additionals.length == 0" class="text-red-500">
              <span v-if="persons.length > 1">{{ getName(person, index) }}: {{ t("Please choose a product") }}</span>
              <span v-if="persons.length == 1">{{ t("Please choose a product") }}</span>
            </div>
            <div v-for="additional in person.additionals" :key="additional.id" class="flex justify-between">
              <span v-if="persons.length > 1">{{ getName(person, index) }}: {{ additional.title }}</span>
              <span v-if="persons.length == 1">{{ additional.title }}</span>
              <span class="shrink-0">
                {{ formatCurrency(additional.price) }}
              </span>
            </div>
            <div v-for="additional in person.extraAdditionals" :key="additional.id" class="flex justify-between">
              <span v-if="persons.length > 1">{{ getName(person, index) }}: {{ additional.title }}</span>
              <span v-if="persons.length == 1">{{ additional.title }}</span>
              <span class="shrink-0">
                {{ formatCurrency(additional.price) }}
              </span>
            </div>
          </div>
        </div>
        <div class="mt-2.5 flex justify-between border-t border-dark-5 py-2.5">
          <span class="text-sm font-semibold">{{ t("Total") }}</span>
          <span class="text-right">{{ formatCurrency(total) }}</span>
        </div>
      </div>
      <div class="mt-5 flex w-full flex-col">
        <button v-if="step == 1" type="button" class="btn btn-red" @click="toStep2()">
          {{ t("Continue").toUpperCase() }}
        </button>
        <div v-show="step == 2" class="mb-2 flex items-center text-dark-1">
          <input
            :id="uniqueId('calculator-conditions')"
            v-model="conditionsAccepted"
            type="checkbox"
            class="conditions-input mr-2.5"
          />
          <label
            :for="uniqueId('calculator-conditions')"
            :class="'rich-text' + (errorConditions ? ' text-red-500' : '')"
            v-html="termsConditions"
          ></label>
        </div>
        <p v-for="(error, index) in freeformErrors" :key="index" class="mb-2.5 text-sm text-red-500">
          {{ error }}
        </p>
        <button v-if="step == 2" type="button" class="btn btn-red" :disabled="submitting" @click="submit()">
          {{ t("Receive").toUpperCase() }}
        </button>
      </div>
    </div>
  </div>
</template>
<script>
import CalculatorPersonAdditional from "./CalculatorPersonAdditional.vue";
import SiteLoader from "./SiteLoader.vue";
import validateEmail from "filter-validate-email";

export default {
  components: {
    CalculatorPersonAdditional,
    SiteLoader,
  },
  props: {
    initialGender: {
      type: String,
    },
    initialPostcode: {
      type: Object,
    },
    termsConditions: {
      type: String,
      required: true,
    },
    freeformSubmit: {
      type: Function,
      required: true,
    },
    submitted: {
      type: Boolean,
      required: true,
    },
    submitting: {
      type: Boolean,
      required: true,
    },
    submittedHtml: {
      type: String,
    },
    freeformErrors: {
      type: Array,
    },
    typeId: {
      type: Number,
      required: true,
    },
    typeTitle: {
      type: String,
      required: true,
    },
    otherTypeIds: {
      type: Array,
    },
  },
  data() {
    return {
      persons: [],
      additionals: [],
      errors: {},
      selectedPerson: 0,
      conditionsAccepted: false,
      errorConditions: false,
      step: 1,
      id: Math.floor(Math.random() * 10000),
    };
  },
  computed: {
    total() {
      let total = 0;
      for (let person of this.persons) {
        for (let add of person.additionals) {
          total += add.price;
        }
        for (let add of person.extraAdditionals) {
          total += add.price;
        }
      }
      return total;
    },
  },
  created() {
    this.addPerson();
  },
  methods: {
    uniqueId(string) {
      return string + "-" + this.id;
    },
    submit() {
      this.validateStep2();
      if (Object.keys(this.errors).length == 0 && this.conditionsAccepted) {
        this.freeformSubmit({
          persons: this.persons.map((p) => {
            return [
              p.firstName + " " + p.lastName,
              p.gender,
              p.email,
              p.postcode.label,
              p.additionals.map((o) => o.title).join(", "),
              p.extraAdditionals.map((o) => o.title).join(", "),
            ];
          }),
          total: this.total,
        });
      }
    },
    getName(person, index) {
      if (person.firstName && person.lastName) {
        return `${person.firstName} ${person.lastName}`;
      }
      return this.t("Person {number}", { number: index + 1 });
    },
    addPerson() {
      let uniqueId = Math.floor(Math.random() * 10000);
      this.persons.push({
        uniqueId: uniqueId,
        additionals: [],
        extraAdditionals: [],
        postcode: this.initialPostcode,
        gender: this.initialGender,
        firstName: "",
        lastName: "",
        email: "",
      });
      this.selectedPerson = uniqueId;
    },
    removePerson(uniqueId) {
      this.selectedPerson = this.persons[0].uniqueId;
      this.persons = this.persons.filter((a) => a.uniqueId != uniqueId);
    },
    updatePerson(event) {
      this.persons = this.persons.map((a) => {
        if (a.uniqueId == this.selectedPerson) {
          return { ...a, ...event };
        }
        return a;
      });
    },
    toStep2() {
      this.validateStep1();
      if (Object.keys(this.errors).length == 0) {
        this.step = 2;
      }
    },
    validateStep1() {
      let errors = {};
      for (let index in this.persons) {
        let person = this.persons[index];
        if (!person.additionals) {
          errors[person.uniqueId] = errors[person.uniqueId] || [];
          errors[person.uniqueId].push("additionals");
        }
      }
    },
    validateStep2() {
      let errors = {};
      for (let index in this.persons) {
        let person = this.persons[index];
        for (let field of ["firstName", "lastName", "email"]) {
          if (!person[field]) {
            errors[person.uniqueId] = errors[person.uniqueId] || [];
            errors[person.uniqueId].push(field);
          }
        }
        if (person.email && !validateEmail(person.email)) {
          errors[person.uniqueId] = errors[person.uniqueId] || [];
          errors[person.uniqueId].push("email");
        }
      }
      this.errors = errors;
      this.errorConditions = !this.conditionsAccepted;
    },
  },
};
</script>
